<script setup>
import axios from 'axios';
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, toRef, watch } from 'vue';
import { compactDate } from '../../../helpers/usDate';
import { DATE_MESSAGES, localDateTime } from '../../../helpers/dateTimeInput';
import { EditorContent, useEditor } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import { CharacterCount } from '@tiptap/extensions';
import { BANNER_COLORS, PORTAL_DESTINATIONS, isValidHttpUrl, richTextLength, sanitizeRichHtml } from '../../../helpers/bannerHelper';
import PortalBanner from '../../portal/PortalBanner.vue';
import XlButton from '../../xl/XlButton.vue';
import XlCard from '../../xl/XlCard.vue';
import XlDateTimeField from '../../xl/XlDateTimeField.vue';
import XlField from '../../xl/XlField.vue';
import XlSelect from '../../xl/XlSelect.vue';
import ContentPageShell from '../../content/ContentPageShell.vue';
import ManageAccessModal from '../../resource-access/ManageAccessModal.vue';
import useHubBrokers from '../../../composables/useHubBrokers';
import useHubSegments from '../../../composables/useHubSegments';
import useStagedImage from '../../../composables/useStagedImage';
import { accessSummaryLine, bookFor, ownBookOf } from '../../../composables/useResourceAccess';
import { useUnsavedChanges } from '../../../composables/useUnsavedChanges';
import { useAuthStore } from '../../../stores/auth';

/**
 * The banner editor. Title, rich body, CTA, tone, colour, window, and who
 * sees it — with one honest question before the save: which live banner this
 * one pushes aside, and for how many people. No priority: the newest banner
 * wins (XN-4564).
 */
const BODY_MAX = 300;

const TONE_OPTIONS = [
  { value: '', label: 'No tone' },
  { value: 'safety', label: 'Safety' },
  { value: 'renewal', label: 'Renewal' },
  { value: 'announcement', label: 'Announcement' },
];

const props = defineProps({
  banner: { type: Object, default: null },
  // Hub members [{ id, label, email, img }] for the audience picker.
  members: { type: Array, default: () => [] },
  // Only a live banner can be ended now, and only the list knows which is.
  live: { type: Boolean, default: false },
  // The server's clock minus ours, as the list measured it. Whether a banner
  // is live is decided by the server (`starts_at <= now`), so a client running
  // a minute fast would otherwise schedule one nobody asked to schedule.
  nowOffsetMs: { type: Number, default: 0 },
  // Whether the member home page shows banners at all once this save lands.
  // A banner saved while the Banners block is off is live, correct, and seen
  // by nobody.
  bannersVisible: { type: Boolean, default: true },
});

const emit = defineEmits(['close', 'saved', 'delete', 'endNow']);

const serverNow = () => Date.now() + props.nowOffsetMs;

/**
 * "Post a banner" posts a banner: a new one starts now unless you say
 * otherwise. It used to default to the next full hour, which is always in the
 * future — so a banner posted at 14:20 was invisible until 15:00, with nothing
 * on the form or in the confirmation saying so.
 *
 * Seconds are dropped rather than kept: the picker's granularity is minutes,
 * and a start half a minute in the past is what makes it live on arrival.
 */
const startingNow = () => {
  const d = new Date(serverNow());
  d.setSeconds(0, 0);
  return d.toISOString();
};

const formDefaults = () => ({
  title: '',
  body: '',
  cta_label: '',
  cta_url: '',
  image_url: '',
  color: 'amber',
  tone: '',
  starts_at: startingNow(),
  ends_at: null,
});

const initialForm = () => Object.fromEntries(
  Object.entries(formDefaults()).map(([key, fallback]) => [key, props.banner?.[key] ?? fallback]),
);

const everyone = () => ({ all_participants: true, member_ids: [], segment_ids: [], broker_ids: [] });
const accessOf = (source) => (source
  ? {
    all_participants: !!source.all_participants,
    member_ids: [...(source.member_ids || [])],
    segment_ids: [...(source.segment_ids || [])],
    broker_ids: [...(source.broker_ids || [])],
  }
  : everyone());
const isEveryone = (a) => !a || a.all_participants;

const form = reactive(initialForm());
const access = ref(accessOf(props.banner?.access));
const initialSnapshot = JSON.stringify({ form: initialForm(), access: accessOf(props.banner?.access) });
const ctaUrlTouched = ref(false);

const CTA_TARGETS = [
  { value: 'web', label: 'A web address' },
  ...PORTAL_DESTINATIONS.map((d) => ({ value: d.path, label: `Portal: ${d.label}` })),
];
const isPortalPage = (url) => PORTAL_DESTINATIONS.some((d) => d.path === url);
const ctaTarget = ref(isPortalPage(form.cta_url) ? form.cta_url : 'web');
// A portal page writes its path; going back to a web address must not keep
// that path as if it were one.
watch(ctaTarget, (target) => {
  if (target !== 'web') form.cta_url = target;
  else if (isPortalPage(form.cta_url)) form.cta_url = '';
});

const imageInput = ref(null);
const {
  uploading: imageUploading, error: imageError, upload, remove: removeImage, keep: keepImage, hold: holdImage, release: releaseImage,
} = useStagedImage('image', toRef(form, 'image_url'));

const uploadImage = (event) => {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (file) upload(file);
};

const showDiscardConfirm = ref(false);
const pickingAudience = ref(false);
const isSaving = ref(false);
const serverErrors = ref({});

// The displacement question is asked once per draft; any edit asks it again.
const displacing = ref([]);
const displacementConfirmed = ref(false);
const displacementFailed = ref(false);
watch([form, access], () => {
  displacing.value = [];
  displacementConfirmed.value = false;
  displacementFailed.value = false;
}, { deep: true });

const { segments, loadHubSegments } = useHubSegments();
onMounted(loadHubSegments);
const { loadHubBrokers } = useHubBrokers();
watch(() => access.value.broker_ids.length, (count) => { if (count) loadHubBrokers(); }, { immediate: true });
const authStore = useAuthStore();
const book = computed(() => bookFor(props.banner, authStore.userData));
const audienceSummary = computed(() => accessSummaryLine(access.value, props.members, segments.value, { book: book.value, me: ownBookOf(authStore.userData) }));

const heading = computed(() => (props.banner ? 'Edit banner' : 'New banner'));

// The audience picker hands back the number it just counted, so the rail can
// say who this reaches without asking again.
const audienceReach = ref(props.banner?.reach ?? null);

// Seed from existing body: in edit mode canSave must be true before the
// first editor keystroke (onUpdate hasn't fired yet).
const bodyLength = ref(richTextLength(form.body));

// Rich-text body (XN-4318). Tiptap handles editing and paste (its parse
// rules map Word/GDocs style-based formatting onto marks); getHTML() output
// (<p>/<strong>/<em>) is normalized through sanitizeRichHtml to the stored
// canonical form — b/i/u/a inline tags with \n line breaks — because the
// server sanitizer unwraps unknown tags, which would silently eat <p>
// paragraph breaks. \n maps back to <br> when loading the editor.
const editor = useEditor({
  content: (form.body || '').replace(/\n/g, '<br>'),
  extensions: [
    StarterKit.configure({
      heading: false,
      blockquote: false,
      bulletList: false,
      orderedList: false,
      codeBlock: false,
      horizontalRule: false,
      code: false,
      strike: false,
      link: { openOnClick: false }
    }),
    CharacterCount.configure({ limit: BODY_MAX })
  ],
  onUpdate: ({ editor: e }) => {
    form.body = sanitizeRichHtml(e.getHTML());
    bodyLength.value = e.storage.characterCount.characters();
  }
});

// Test hook: jsdom lacks full contenteditable support.
const setBodyForTest = (html, length) => {
  form.body = html;
  bodyLength.value = length;
};

const setLink = () => {
  const previous = editor.value?.getAttributes('link').href;
  const url = window.prompt('Link URL (http/https)', previous || 'https://');
  if (url === null) return;

  let href = url.trim();
  if (href === '' || href === 'https://') {
    editor.value?.chain().focus().unsetLink().run();
    return;
  }
  if (!/^https?:\/\//i.test(href)) href = `https://${href}`;
  if (!isValidHttpUrl(href)) return;
  editor.value?.chain().focus().setLink({ href }).run();
};

const isDirty = computed(() => JSON.stringify({ form: { ...form }, access: access.value }) !== initialSnapshot);
useUnsavedChanges({ key: 'banner-editor', dirty: isDirty, onSectionHome: () => emit('close') });

const ctaUrlError = computed(() => {
  if (!form.cta_label) return '';
  if (!form.cta_url) return 'A link is required when the button label is set.';
  if (isPortalPage(form.cta_url)) return '';
  if (!isValidHttpUrl(form.cta_url)) return 'The link must start with http:// or https://.';
  return '';
});

const onCtaUrlBlur = () => {
  ctaUrlTouched.value = true;
  const url = (form.cta_url || '').trim();
  if (url && !/^https?:\/\//i.test(url) && isValidHttpUrl(`https://${url}`)) {
    form.cta_url = `https://${url}`;
  }
};

// One that has run keeps its start and may have its end moved anywhere; one
// that has not would never show, yet end the banner people see. The server
// holds both (XN-4564). Asked each time the dates are checked, not cached:
// the start can pass with the editor open.
const hasRun = () => !!props.banner?.starts_at && Date.parse(props.banner.starts_at) <= serverNow();

const startError = computed(() => {
  // Read before asking hasRun(), so a change to the start is always re-checked.
  const start = Date.parse(form.starts_at);
  return hasRun() && start !== Date.parse(props.banner.starts_at) ? "It has already started, so its start can't change." : '';
});

const datesError = computed(() => {
  if (!form.starts_at) return 'Start is required.';
  if (form.ends_at && Date.parse(form.ends_at) <= Date.parse(form.starts_at)) return DATE_MESSAGES.endBeforeStart;
  if (form.ends_at && !hasRun() && Date.parse(form.ends_at) <= serverNow()) return 'That end has already passed. Pick a later one.';
  return '';
});

const whenLabel = (iso) => compactDate(iso, { time: true });

// Not a live clock: these answer "what happens when I press Save", which is a
// question asked of the value in front of you, once.
const startsInFuture = computed(() => Date.parse(form.starts_at) > serverNow());
const endsInPast = computed(() => !!form.ends_at && Date.parse(form.ends_at) <= serverNow());

const canSave = computed(() =>
  form.title.trim() !== '' &&
  bodyLength.value > 0 &&
  bodyLength.value <= BODY_MAX &&
  !ctaUrlError.value &&
  !datesError.value &&
  !startError.value &&
  !imageUploading.value
);

const previewBanner = computed(() => ({
  id: props.banner?.id ?? 0,
  ...form,
  tone: form.tone || null,
  cta_label: form.cta_label || '',
  cta_url: form.cta_url || ''
}));

const errorFor = (field) => {
  const list = serverErrors.value?.[field];
  return Array.isArray(list) ? list[0] : '';
};

const people = (n) => `${new Intl.NumberFormat().format(n)} ${n === 1 ? 'person' : 'people'}`;

const draft = () => ({
  ...form,
  tone: form.tone || null,
  cta_label: form.cta_label || null,
  cta_url: form.cta_url || null,
  image_url: form.image_url || null,
});

const onAudience = (chosen) => {
  access.value = accessOf(chosen);
  if (chosen?.reach !== undefined) audienceReach.value = chosen.reach;
  pickingAudience.value = false;
};

const accessChanged = computed(() => JSON.stringify(access.value) !== JSON.stringify(accessOf(props.banner?.access)));

/**
 * What the save actually did. 'Saved successfully' was equally true of a banner
 * nobody would see for another hour, of one whose end date had passed, and of
 * one saved while the Banners block was switched off.
 */
const savedMessage = () => {
  const now = serverNow();
  const startsFuture = Date.parse(form.starts_at) > now;
  const endsPast = !!form.ends_at && Date.parse(form.ends_at) <= now;

  if (!props.bannersVisible) return 'Banner saved — but Banners are switched off for this hub, so nobody sees it yet.';
  if (startsFuture) return `Banner scheduled for ${whenLabel(form.starts_at)}.`;
  if (endsPast) return 'Banner saved — its end date has passed, so nobody sees it.';
  return form.ends_at ? `Banner is live until ${whenLabel(form.ends_at)}.` : 'Banner is live.';
};

// Every way out of a save: the image it sent is no longer the save's to hold.
const stopSaving = () => {
  isSaving.value = false;
  releaseImage();
};

const pushedAside = async (api) => {
  if (displacementConfirmed.value) return false;
  try {
    displacementFailed.value = false;
    const { data } = await axios.post(`${api}/event-banners/displacement`, {
      id: props.banner?.id ?? null,
      starts_at: form.starts_at,
      ends_at: form.ends_at,
      access: access.value,
    });
    const rows = data?.data ?? [];
    if (!rows.length) return false;
    displacing.value = rows;
    displacementConfirmed.value = true;
    return true;
  } catch (error) {
    console.error(error);
    displacementFailed.value = true;
    return false;
  }
};

const sendBanner = async (api) => {
  const base = `${api}/event-banners`;
  const res = props.banner?.id
    ? await axios.put(`${base}/${props.banner.id}`, draft())
    : await axios.post(base, draft());
  return res.data?.data ?? null;
};

const reportSaveFailure = (error) => {
  // ApiResponseTrait::error() puts the Laravel validator's MessageBag
  // (field => [messages]) under `message`, not `data`.
  serverErrors.value = error.response?.data?.message ?? {};
  window.loadNotification?.({ message: 'An error occurred while saving the banner.', error: true });
  stopSaving();
};

// A banner meant for some people must not go out to everyone, so a failed audience write undoes it.
const undoNewBanner = async (api, id, saved) => {
  try {
    await axios.delete(`${api}/event-banners/${id}`);
    window.loadNotification?.({ message: "Couldn't set who sees it, so the banner was not saved. Try again.", error: true });
  } catch (undoError) {
    console.error(undoError);
    window.loadNotification?.({ message: 'The banner was saved, but everyone sees it. Set who sees it from the list.', error: true });
    keepImage();
    emit('saved', saved);
    emit('close');
  }
};

const needsAccessWrite = (id) => id && (props.banner?.id ? accessChanged.value : !isEveryone(access.value));

const writeAccess = async (api, saved) => {
  const editing = !!props.banner?.id;
  const id = saved?.id ?? props.banner?.id;
  if (!needsAccessWrite(id)) return false;
  try {
    await axios.put(`${api}/content-access/banner/${id}`, access.value);
    return false;
  } catch (error) {
    console.error(error);
    if (editing) {
      window.loadNotification?.({ message: 'The banner was saved, but who sees it did not change. Try again from the list.', error: true });
      return false;
    }
    await undoNewBanner(api, id, saved);
    return true;
  }
};

const save = async () => {
  if (!canSave.value || isSaving.value) return;

  const api = window.store.event_backend_api;
  isSaving.value = true;
  holdImage();
  serverErrors.value = {};

  // Advisory: which banner this pushes aside. A failed check never blocks.
  if (await pushedAside(api)) {
    stopSaving();
    scrollRailToBottom();
    return;
  }

  let saved = null;
  try {
    saved = await sendBanner(api);
  } catch (error) {
    reportSaveFailure(error);
    return;
  }

  if (await writeAccess(api, saved)) {
    stopSaving();
    return;
  }

  window.loadNotification?.({ message: savedMessage(), error: !props.bannersVisible });
  keepImage();
  emit('saved', saved);
  emit('close');
  stopSaving();
};

const cancel = () => {
  // The save is still deciding what is kept; leaving now could lose its image.
  if (isSaving.value) return;
  if (isDirty.value) {
    showDiscardConfirm.value = true;
    return;
  }
  emit('close');
};

const onKeydown = (e) => {
  if (e.key === 'Escape' && !pickingAudience.value) cancel();
};

const previewWrap = ref(null);

/**
 * On the page layout the fields card is the only thing that scrolls: the
 * heading and the preview rail stay put. How tall it may be depends on where
 * it lands under the page's own header, which CSS cannot read — so it is
 * measured from the top of the document once the page has settled, and again
 * whenever the window changes size.
 */
const fieldsEl = ref(null);
const fieldsMaxHeight = ref('');
const canScroll = ref(false);
const atEnd = ref(false);

// Whether there is more form below the fold, and whether you have reached it.
const readScroll = () => {
  const el = fieldsEl.value;
  if (!el) return;
  canScroll.value = el.scrollHeight - el.clientHeight > 8;
  atEnd.value = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
};

const measureFields = async () => {
  if (!fieldsEl.value) return;
  const top = fieldsEl.value.getBoundingClientRect().top + window.scrollY;
  fieldsMaxHeight.value = `max(320px, calc(100dvh - ${Math.round(top)}px - 24px))`;
  await nextTick();
  readScroll();
};

// An error appearing or the displacement notice arriving changes how much
// form there is; the cue has to keep up with it.
watch([form, displacing, serverErrors], () => { nextTick(readScroll); }, { deep: true });

// The rail is the scroller that holds the preview and the displacement
// notice; both can land below its fold.
const scrollRailToBottom = async () => {
  await nextTick();
  const scroller = previewWrap.value?.closest('.banner-form__rail');
  if (typeof scroller?.scrollTo === 'function') scroller.scrollTo({ top: scroller.scrollHeight, behavior: 'smooth' });
};

// A class rather than an inline style: a ManageAccessModal opening over the
// editor sets and then clears `body.style.overflow` when it closes, which
// would hand the page back its scrollbar while the editor is still open.
const PAGE_LOCK = 'xl-content-editor-open';

onMounted(async () => {
  document.addEventListener('keydown', onKeydown);
  window.addEventListener('resize', measureFields);
  document.body.classList.add(PAGE_LOCK);
  await nextTick();
  measureFields();
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown);
  window.removeEventListener('resize', measureFields);
  document.body.classList.remove(PAGE_LOCK);
});

defineExpose({ form, access, canSave, save, cancel, displacing, showDiscardConfirm, onCtaUrlBlur, setLink, setBodyForTest, editor, startsInFuture });
</script>

<template>
  <ContentPageShell :title="heading" back-label="Back to banners" @back="cancel">
    <div class="banner-form banner-form--page">
      <div
        ref="fieldsEl"
        class="banner-form__fields"
        :style="fieldsMaxHeight ? { maxHeight: fieldsMaxHeight } : null"
        @scroll="readScroll"
      >
        <!-- A new banner opens with the cursor here, so the Overview's
             "Post a banner" tile lands you typing rather than hunting. -->
        <XlField
          v-model="form.title"
          label="Title"
          name="title"
          required
          maxlength="100"
          :error="errorFor('title')"
          :autofocus="!props.banner"
        />

        <div class="banner-form__field">
          <span class="banner-form__label">Body <span class="banner-form__required">*</span></span>
          <div class="banner-form__editor-box">
            <div v-if="editor" class="banner-form__toolbar" role="toolbar" aria-label="Text formatting">
              <button type="button" :class="{ active: editor.isActive('bold') }" aria-label="Bold" @click="editor.chain().focus().toggleBold().run()"><b>B</b></button>
              <button type="button" :class="{ active: editor.isActive('italic') }" aria-label="Italic" @click="editor.chain().focus().toggleItalic().run()"><i>I</i></button>
              <button type="button" :class="{ active: editor.isActive('underline') }" aria-label="Underline" @click="editor.chain().focus().toggleUnderline().run()"><u>U</u></button>
              <button type="button" :class="{ active: editor.isActive('link') }" aria-label="Link" @click="setLink">🔗</button>
            </div>
            <editor-content :editor="editor" class="banner-form__editor" />
          </div>
          <div class="banner-form__foot">
            <span v-if="errorFor('body')" class="banner-form__error">{{ errorFor('body') }}</span>
            <span class="banner-form__counter">{{ bodyLength }} / {{ BODY_MAX }} characters maximum</span>
          </div>
        </div>

        <div class="banner-form__field">
          <span class="banner-form__label">Image — optional</span>
          <div class="banner-form__image">
            <img v-if="form.image_url" :src="form.image_url" alt="" class="banner-form__image-thumb">
            <XlButton variant="ghost" size="sm" :loading="imageUploading" :disabled="isSaving" data-testid="banner-image-upload" @click="imageInput?.click()">
              Upload an image
            </XlButton>
            <XlButton v-if="form.image_url" variant="ghost" size="sm" :disabled="isSaving" data-testid="banner-image-remove" @click="removeImage">Remove</XlButton>
            <input ref="imageInput" type="file" accept="image/png,image/jpeg,image/gif" class="banner-form__file" aria-label="Image" data-testid="banner-image-input" @change="uploadImage">
          </div>
          <div class="banner-form__hint">Shown beside the text on desktop and above it on a phone. PNG, JPG or GIF, up to 10 MB.</div>
          <div v-if="imageError || errorFor('image_url')" class="banner-form__error">{{ imageError || errorFor('image_url') }}</div>
        </div>

        <div class="banner-form__row">
          <XlField v-model="form.cta_label" label="Button label" name="cta_label" maxlength="25" :error="errorFor('cta_label')" />
          <div class="banner-form__field">
            <span class="banner-form__label">Tag</span>
            <XlSelect v-model="form.tone" :options="TONE_OPTIONS" name="tone" aria-label="Tag" />
            <div class="banner-form__hint">Said above the headline so people know what kind of message it is</div>
            <div v-if="errorFor('tone')" class="banner-form__error">{{ errorFor('tone') }}</div>
          </div>
        </div>

        <div class="banner-form__field">
          <span class="banner-form__label">Button goes to</span>
          <XlSelect v-model="ctaTarget" :options="CTA_TARGETS" name="cta_target" aria-label="Button goes to" data-testid="banner-cta-target" />
        </div>
        <XlField
          v-if="ctaTarget === 'web'"
          v-model="form.cta_url"
          type="url"
          label="Web address"
          name="cta_url"
          placeholder="https://…"
          hint="Opens in a new tab so policyholders don't lose their place in the portal."
          :error="(ctaUrlError && (ctaUrlTouched || form.cta_url)) ? ctaUrlError : errorFor('cta_url')"
          @blur="onCtaUrlBlur"
        />

        <div class="banner-form__row">
          <XlDateTimeField label="Starts" kind="datetime" required :model-value="localDateTime(form.starts_at)" :error="startError || errorFor('starts_at')" @update:model-value="(v) => { form.starts_at = v ? new Date(v).toISOString() : null; }" />
          <XlDateTimeField label="Ends — optional" kind="datetime" :model-value="localDateTime(form.ends_at)" :error="errorFor('ends_at')" @update:model-value="(v) => { form.ends_at = v ? new Date(v).toISOString() : null; }" />
        </div>
        <div v-if="datesError && form.ends_at" class="banner-form__error">{{ datesError }}</div>
        <!-- A start in the future looks exactly like a start now, and Save is
             the same button either way — so the form says which one this is. -->
        <p v-if="!datesError" class="banner-form__schedule" :class="{ 'banner-form__schedule--future': startsInFuture }">
          <template v-if="startsInFuture">Scheduled — nobody sees it until {{ whenLabel(form.starts_at) }}.</template>
          <template v-else-if="endsInPast">Its end date has passed, so nobody will see it.</template>
          <template v-else>Goes live as soon as you save.</template>
        </p>

        <div class="banner-form__field">
          <span class="banner-form__label">Color</span>
          <div class="banner-form__swatches">
            <button
              v-for="color in BANNER_COLORS"
              :key="color"
              type="button"
              class="banner-form__colour"
              :class="[`banner-form__colour--${color}`, { active: form.color === color }]"
              :aria-label="`${color} banner color`"
              :aria-pressed="form.color === color ? 'true' : 'false'"
              @click="form.color = color"
            >{{ color.charAt(0).toUpperCase() + color.slice(1) }}</button>
          </div>
          <div class="banner-form__hint">
            Pick a color that matches the tone. Red and amber for anything urgent — if everything is red,
            nothing is.
          </div>
        </div>

        <div v-show="canScroll && !atEnd" class="banner-form__more" aria-hidden="true">
          <span class="banner-form__more-cue">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9.5 6 6 6-6" /></svg>
          </span>
        </div>
      </div>

      <aside class="banner-form__rail" :style="fieldsMaxHeight ? { maxHeight: fieldsMaxHeight } : null">
        <div ref="previewWrap" class="banner-form__preview">
          <h3 class="banner-form__heading">What policyholders will see</h3>
          <PortalBanner :banner="previewBanner" preview />
        </div>

        <hr class="banner-form__rule">

        <div class="banner-form__who">
            <h3 class="banner-form__heading">Who sees it</h3>
            <div class="banner-form__who-row">
              <span class="banner-form__who-icon" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 19v-1.6a3.4 3.4 0 0 0-3.4-3.4H6.4A3.4 3.4 0 0 0 3 17.4V19" /><circle cx="9.5" cy="7.5" r="3.1" /><path d="M21 19v-1.6a3.4 3.4 0 0 0-2.6-3.3M15.4 4.6a3.4 3.4 0 0 1 0 6.1" /></svg>
              </span>
              <span class="banner-form__who-text">
                <b>{{ audienceSummary }}</b>
                <small v-if="audienceReach !== null">{{ people(audienceReach) }}</small>
              </span>
              <XlButton variant="ghost" size="sm" @click="pickingAudience = true">Change</XlButton>
            </div>
          <p class="banner-form__note">
            A storm banner only makes sense to people in the storm's path. Aiming it at a segment keeps
            everyone else's portal calm.
          </p>
          <!-- The block switch lives on the list, which is stood down while the
               editor is open — so the one place it still matters says it. -->
          <p v-if="!bannersVisible" class="banner-form__blocked" role="alert">
            Banners are switched off for this hub. Whoever you aim this at, nobody sees it until the
            Banners switch is back on.
          </p>
        </div>

        <XlCard v-if="displacing.length" padded tone="warning" class="banner-form__displacing" role="alert">
          <p v-for="row in displacing" :key="row.id">This will replace '{{ row.title }}' for {{ people(row.affected) }}.</p>
          <b>Save again to confirm.</b>
        </XlCard>
        <p v-else-if="displacementFailed" class="banner-form__hint">Couldn't check who this reaches.</p>

        <hr class="banner-form__rule">
        <div class="banner-form__page-actions">
            <XlButton :disabled="!canSave || isSaving" @click="save">{{ displacing.length ? 'Save anyway' : 'Save banner' }}</XlButton>
            <XlButton variant="ghost" @click="cancel">Cancel</XlButton>
            <XlButton v-if="props.banner && props.live" variant="ghost" @click="emit('endNow')">End it now</XlButton>
          <XlButton v-if="props.banner" variant="ghost-danger" @click="emit('delete')">Delete this banner</XlButton>
        </div>
      </aside>

      <div v-if="showDiscardConfirm" class="banner-form__discard">
        <p>Discard unsaved changes?</p>
        <div>
          <XlButton variant="ghost" size="sm" @click="showDiscardConfirm = false">Keep editing</XlButton>
          <XlButton variant="danger" size="sm" @click="emit('close')">Discard</XlButton>
        </div>
      </div>
    </div>

    <ManageAccessModal
      v-if="pickingAudience"
      :resource="{ name: form.title, access }"
      :members="members"
      :book="book"
      subject-type="banner"
      @close="pickingAudience = false"
      @saved="onAudience"
    />
  </ContentPageShell>
</template>

<style scoped lang="scss">
.banner-form {
  display: flex;
  flex-direction: column;
  row-gap: 18px;

  /* The page layout is the same fields in the same order — CSS moves the
     preview rail beside them rather than a second copy of the markup. */
  &--page {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    column-gap: 22px;
    align-items: start;

    > * { grid-column: 1; }

    > .banner-form__fields {
      display: flex;
      flex-direction: column;
      row-gap: 18px;
      padding: 26px 28px;
      border: 1px solid var(--xl-color-outline-variant, #e7e0ec);
      border-radius: var(--xl-radius-lg, 16px);
      background: #fff;
      /* The one scroller on the page; the rail beside it holds still. */
      position: sticky;
      top: 16px;
      overflow-y: auto;
      overscroll-behavior: contain;
    }

    /* The rail is short by design, but a displacement notice can grow it —
       it scrolls itself rather than pushing the page past the fold. */
    > .banner-form__rail { overflow-y: auto; overscroll-behavior: contain; }

    > .banner-form__rail {
      grid-column: 2;
      grid-row: 1 / span 99;
      position: sticky;
      top: 16px;
      display: flex;
      flex-direction: column;
      gap: 16px;
      padding: 20px;
      border: 1px solid var(--xl-color-outline-variant, #e7e0ec);
      border-radius: var(--xl-radius-lg, 16px);
      background: #fff;
    }
  }

  &__rail,
  &__fields {
    display: contents;
  }

  &__heading {
    margin: 0 0 12px;
    font-size: 15px;
    font-weight: 600;
    color: var(--xl-color-primary-text, #1d1b20);
  }

  &__rule {
    width: 100%;
    height: 1px;
    margin: 0;
    border: 0;
    background: var(--xl-color-outline-variant, #e7e0ec);
  }

  &__who-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__who-icon {
    width: 42px;
    height: 42px;
    flex: 0 0 42px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--xl-radius-md, 12px);
    background: var(--xl-color-success-container, #e4f3eb);
    color: var(--xl-color-success, #166b48);
  }

  &__who-text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;

    small { color: var(--xl-color-secondary-text, #49454f); }
  }

  &__note {
    margin: 12px 0 0;
    font-size: var(--xl-type-body-small-size, 12.5px);
    color: var(--xl-color-secondary-text, #49454f);
  }

  &__blocked {
    margin: 12px 0 0;
    padding: 10px 12px;
    border-radius: var(--xl-radius-sm, 8px);
    background: #fdf2e2;
    font-size: var(--xl-type-body-small-size, 12.5px);
    color: #8a5200;
  }

  &__schedule {
    margin: -6px 0 0;
    font-size: var(--xl-type-body-small-size, 12.5px);
    color: var(--xl-color-secondary-text, #49454f);

    &--future {
      font-weight: 600;
      color: #8a5200;
    }
  }

  &__page-actions {
    display: flex;
    flex-direction: column;
    gap: 9px;

    :deep(.xl-btn) { width: 100%; }
  }

  @media (max-width: 900px) {
    &--page {
      display: flex;
      flex-direction: column;

      > .banner-form__rail { position: static; }
      /* Stacked, the page is the scroller again — a card that scrolls inside
         a column that also scrolls is worse than either. */
      > .banner-form__fields {
        padding: 0;
        border: 0;
        background: none;
        position: static;
        max-height: none !important;
        overflow: visible;
      }

      > .banner-form__rail { max-height: none !important; overflow: visible; }

      .banner-form__more { display: none; }
    }
  }

  &__image { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  &__image-thumb { width: 72px; height: 48px; object-fit: cover; border-radius: 8px; }
  &__file { display: none; }

  &__label {
    display: block;
    margin-bottom: 6px;
    font-size: var(--xl-type-body-medium-size, 14px);
    font-weight: 600;
    color: var(--xl-color-primary-text, #1d1b20);
  }

  &__required { color: var(--xl-color-error, #ba1a1a); }

  &__editor-box {
    border: 1px solid var(--xl-color-outline, #cac4d0);
    border-radius: var(--xl-radius-md, 12px);
    padding: 12px;

    &:focus-within { border-color: var(--xl-color-primary, #5e2f78); }
  }

  &__toolbar {
    display: flex;
    gap: 4px;
    margin-bottom: 8px;

    button {
      width: 32px;
      height: 32px;
      border: 1px solid var(--xl-color-outline-variant, #e7e0ec);
      border-radius: var(--xl-radius-sm, 8px);
      background: #fff;
      cursor: pointer;

      &.active { background: var(--xl-color-primary-container, #f3deff); }
    }
  }

  &__editor {
    :deep(.tiptap) {
      min-height: 96px;
      outline: none;
      font-size: 16px;
      line-height: 24px;
      color: var(--xl-color-primary-text, #1e1a1f);
      overflow-wrap: break-word;

      p { margin: 0; }
      a { color: var(--xl-color-primary, #5e2f78); text-decoration: underline; }
    }
  }

  &__hint {
    margin-top: 4px;
    font-size: 12px;
    line-height: 16px;
    color: var(--xl-color-secondary-text, #49454f);
  }

  &__error {
    margin-top: 4px;
    font-size: 12px;
    color: var(--xl-color-error, #b3261e);
  }

  &__row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  &__audience {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 12px 14px;
    border: 1px solid var(--xl-color-outline-variant, #e7e0ec);
    border-radius: var(--xl-radius-md, 12px);
    font-size: var(--xl-type-body-medium-size, 14px);
  }

  &__displacing {
    margin-bottom: 0;
    font-size: var(--xl-type-body-medium-size, 14px);

    p { margin: 0 0 4px; }
  }

  &__swatches {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  /* The prototype's colour control is the colour itself, worn by the word. */
  &__colour {
    height: 36px;
    padding: 0 15px;
    border: 0;
    border-radius: var(--xl-radius-sm, 8px);
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;

    &.active { outline: 2px solid currentColor; outline-offset: 2px; }
  }

  &__colour--amber { background: #fdf2e2; color: #8a5200; }
  &__colour--red { background: #fcebeb; color: #a32020; }
  &__colour--blue { background: #e7f1f8; color: #1f5e8c; }
  &__colour--green { background: #e4f3eb; color: #166b48; }
  &__colour--purple { background: #f5eff9; color: #5e2f78; }

  &__foot {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
  }

  /* Sticks to the floor of the scrolling card: a fade over what is cut off,
     and a nudge that there is more form under it. */
  &__more {
    position: sticky;
    bottom: 0;
    height: 0;
    z-index: 1;
    pointer-events: none;

    &::before {
      content: "";
      position: absolute;
      left: -28px;
      right: -28px;
      bottom: -26px;
      height: 60px;
      background: linear-gradient(to top, #fff 34%, rgba(255, 255, 255, 0));
    }
  }

  &__more-cue {
    position: absolute;
    left: 50%;
    bottom: -8px;
    display: grid;
    place-items: center;
    width: 30px;
    height: 30px;
    margin-left: -15px;
    border-radius: 50%;
    background: #fff;
    color: var(--xl-color-primary, #5e2f78);
    box-shadow: var(--xl-shadow-2, 0 3px 10px rgba(28, 23, 32, 0.12));
    animation: banner-more-bob 1.7s ease-in-out infinite;
  }

  &__counter {
    margin-left: auto;
    flex: none;
    font-size: var(--xl-type-body-small-size, 12.5px);
    color: var(--xl-color-outline, #867d8d);
  }




  &__discard {
    position: sticky;
    bottom: 0;
    z-index: 60;
    padding: 14px 16px;
    border-radius: var(--xl-radius-md, 12px);
    background: #fff;
    box-shadow: 0 1px 8px rgba(0, 0, 0, 0.25);
    display: flex;
    align-items: center;
    justify-content: space-between;

    p { margin: 0; font-weight: 600; }
    div { display: flex; gap: 8px; }
  }
}

@keyframes banner-more-bob {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(4px); }
}

@media (prefers-reduced-motion: reduce) {
  .banner-form__more-cue { animation: none; }
}
</style>

<style>
/* Set while a full-page content editor is open: the page itself must not
   scroll, only the card inside it. A class rather than an inline style, so a
   modal opening over the editor cannot clear it again on its way out. */
body.xl-content-editor-open {
  overflow: hidden;
}
</style>
